/* eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useMemo, useState } from 'react';
import { CheckBox, CustomButtonGroup, CustomInput, SelectDropDown, useAppDispatch, useAppSelector, useEffectOnce, } from '../../../globalUtils/globalExports';
import { fetchPeripheralCardRefData, fetchResetCounterRemoteCommandRefData, fetchRuleRemoteCommands, getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports';
import { FormLabel } from '@mui/material';
import { cloneDeep, isEqual } from 'lodash';
import { ExecutionPlan, RemoteCommandAction } from './ExecutionPlan';
import { getRoleId } from '../AdvanceRuleEngineForm';
import './RemoteCommandSidePopover.css';



interface RemoteCommandPopoverProps {
    remoteCommand: null | RemoteCommandAction;
    handleClose: () => void;
    executionPlan: ExecutionPlan;
    handleUpdateExecutionPlan: Function;
    ifOrElse: string;
    nodeId: string;
    parentNodeId: string;
}

const RemoteCommandSidePopover =  ({
    remoteCommand,
    handleClose,
    executionPlan,
    handleUpdateExecutionPlan,
    ifOrElse,
    nodeId,
    parentNodeId

}: RemoteCommandPopoverProps) => {

    const dispatch = useAppDispatch();
    const { ruleEngineRemoteCommands,resetCounterRemoteCommandReferenceData, peripheralCardReferenceData} = useAppSelector(getReferenceDataReducer);
    const roleId = getRoleId();

    const [formData, setFormData] = useState<RemoteCommandAction>({
        type: '',
        nodeId,
        remoteCommandType: '',
        url: '',
        connectorNumber: null,
        connectorScope: null,
        peripheralCardType: null,
    });

    const [initialFormData, setInitialFormData] = useState<RemoteCommandAction>({
        type: '',
        nodeId,
        remoteCommandType: '',
        url: '',
        connectorNumber: null,
        connectorScope: null,
        peripheralCardType: null,
    });

    useEffectOnce(() => {
        if (remoteCommand) {
            const data = cloneDeep(remoteCommand);
            data.remoteCommandType = data?.remoteCommandType?.toUpperCase();
            setInitialFormData(data)
            setFormData(data);
        }
        dispatch(fetchRuleRemoteCommands(roleId));
        dispatch(fetchResetCounterRemoteCommandRefData());
        dispatch(fetchPeripheralCardRefData({chargerId: null}));
    });

    const isFtpUrlRequired = useMemo(() => {
        return formData && formData.remoteCommandType.length > 0 &&
            (['ftp_software_upgrade', 'ftp_log_upload', 'ftp_certificate_upgrade', 'ftp_screen_saver_images_upgrade', 'ftp_customer_logo_update', 'ftp_config_file_update','ftp_peripheral_card_sw_update',
                'FTP_SOFTWARE_UPGRADE', 'FTP_LOG_UPLOAD', 'FTP_CERTIFICATE_UPGRADE', 'FTP_SCREEN_SAVER_IMAGES_UPGRADE', 'FTP_CUSTOMER_LOGO_UPDATE', 'FTP_CONFIG_FILE_UPDATE','FTP_PERIPHERAL_CARD_SW_UPDATE'].indexOf(formData.remoteCommandType) != -1);
    }, [formData])

    const connectorLevelRC = useMemo(() => {
        const connectorLevelCommands = ['DISABLE_CONNECTOR', 'ENABLE_CONNECTOR', 'CALIBRATE_VOLTAGE', 'PLC_SOFTWARE_UPGRADE','PLC_RESET'];
        return new Set(connectorLevelCommands);
    },[])

    const isPeripheralCardRequired = useCallback((remoteCommand: string) => {
        const peripheralCardRemoteCommands = new Set(['FTP_PERIPHERAL_CARD_SW_UPDATE','CARD_RESET']);
        return peripheralCardRemoteCommands.has(remoteCommand);
    },[])

    const connectorLevelPeripheralCardTypes = useMemo(() => {
        return new Set(['2','7']);
    },[])

    const disableButton = useCallback(() => {

        if (isEqual(initialFormData, formData))
            return true;
        else {
            let result = true;
            result = result && formData && formData.remoteCommandType.length > 0;
            if (isFtpUrlRequired) {
                result = result && formData && formData.url !== undefined && formData.url.length > 0
            }
            return !result;
        }
    }, [formData, initialFormData])


    const btnsList = useMemo(() => {
        return [
            {
                buttonText: 'Save',
                buttonId: 'add',
                btnClassName: disableButton() ? 'primary__btn disabled' : 'primary__btn',
                handleClick: (): void => {
                    const data = cloneDeep(formData);
                    const formattedData: RemoteCommandAction = {
                        type: 'Remote Command',
                        nodeId,
                        remoteCommandType: data.remoteCommandType.toLowerCase(),
                        url: data.url,
                        connectorScope: data.connectorScope,
                        peripheralCardType: data.peripheralCardType
                    }
                    const executionData = cloneDeep(executionPlan);
                    const actionIdx = executionData?.actions?.findIndex((executionDataItem) => executionDataItem.flowType === ifOrElse && executionDataItem?.parentNodeId === parentNodeId);
                    const actionItemIndex = executionData?.actions?.[actionIdx]?.list?.findIndex(item => item?.type === 'Remote Command');
                    if(actionItemIndex === -1){
                        executionData?.actions?.[actionIdx]?.list?.push(formattedData)
                    }else{
                        executionData.actions[actionIdx].list[actionItemIndex] = formattedData
                    }
                    handleUpdateExecutionPlan(executionData);
                    handleClose();
                },
                isDisabled: disableButton(),
                buttonVariant: 'filled',
            },
            {
                buttonText: 'Cancel',
                buttonId: 'cancel',
                btnClassName: 'secondary__btn',
                handleClick: (): void => {
                    handleClose()
                },
                isDisabled: false,
                buttonVariant: 'outlined',
            },
        ];
    }, [formData, handleClose]);

    const remoteCommandsRef = useMemo(()=> {
        return ruleEngineRemoteCommands?.filter(item => (item?.id != 'FTP_PERIPHERAL_CARD_SW_UPDATE' && item?.id != 'CARD_RESET'))
    },[ruleEngineRemoteCommands])


    return (
        <>
            <div className='advance__rule__engine__remote__command__side__popover__wrapper'>
                <div className='advance__rule__engine__remote__command__dropdown__wrapper'>
                    <div className='advance__rule__engine__remote__command__side__popover__row__label'>
                        Remote Command*
                    </div>
                    <div>
                        <SelectDropDown
                            selectDropDownId={'advance__rule__engine__remote__command__popover__dropdown'}
                            handleSelectOptionChange={(val) => {
                                setFormData({
                                    ...formData,
                                    remoteCommandType: val?.label,
                                    url: '',
                                    connectorScope: connectorLevelRC?.has(val?.label) ? 'any' : null,
                                    peripheralCardType: null

                                })
                            }}
                            selectOptions={remoteCommandsRef ?? []}
                            selectValue={{ id: formData?.remoteCommandType.toUpperCase(), label: formData?.remoteCommandType.toUpperCase() }}
                        />
                    </div>
        
                    {
                        isFtpUrlRequired && <div className='advance__rule__engine__remote__command__url__wrapper'>
                            <FormLabel className={'rms-form-label'} required>
                                URL
                            </FormLabel>
                            <CustomInput
                                inputPlaceHolder='Type here'
                                handleInputChange={(event) => {
                                    const data = { ...formData };
                                    data.url = event.target.value;
                                    setFormData(data);
                                }}
                                inputValue={formData?.url ?? ''}
                                inputFieldId='advance__rule__engine__remote__command__url'
                                isRequired={true}
                            />
                        </div>
                    }

                    {
                        formData?.remoteCommandType && formData?.remoteCommandType === 'RESET_COUNTER' && <div className='advance__rule__engine__remote__command__url__wrapper'>
                            <FormLabel className={'rms-form-label'} required>
                            Counter Type  
                            </FormLabel>
                            <SelectDropDown 
                                selectValue={resetCounterRemoteCommandReferenceData?.find(item => item?.id == formData?.url)}
                                selectDropDownId={'advance__rule__engine__counter__type-dropdown'}
                                dropDownClassName={'advance__rule__engine__counter__type__dropdown'} 
                                handleSelectOptionChange={(val) => {
                                    const data = { ...formData };
                                    data.url = val?.id;
                                    setFormData(data);
                                } } 
                                selectOptions={resetCounterRemoteCommandReferenceData ?? []}                            
                            />
                        </div>
                    }

                    {
                        formData?.remoteCommandType && isPeripheralCardRequired(formData?.remoteCommandType) && <div className='advance__rule__engine__remote__command__url__wrapper'>
                            <FormLabel className={'rms-form-label'} required>
                            Card Type  
                            </FormLabel>
                            <SelectDropDown 
                                selectValue={peripheralCardReferenceData?.cardTypes?.find(item => item?.id == formData?.peripheralCardType)}
                                selectDropDownId={'advance__rule__engine__counter__type-dropdown'}
                                dropDownClassName={'advance__rule__engine__counter__type__dropdown'} 
                                handleSelectOptionChange={(val) => {
                                    const data = { ...formData };
                                    data.peripheralCardType = val?.id;
                                    if(connectorLevelPeripheralCardTypes.has(val?.id)) data.connectorScope = 'any';
                                    else data.connectorScope = null
                                    setFormData(data);
                                } } 
                                selectOptions={peripheralCardReferenceData?.cardTypes ?? []}                            
                            />
                        </div>
                    }
                </div>

                {
                    (connectorLevelRC?.has(formData?.remoteCommandType) || (formData?.remoteCommandType === 'FTP_PERIPHERAL_CARD_SW_UPDATE' && formData?.peripheralCardType && connectorLevelPeripheralCardTypes.has(formData?.peripheralCardType))) && <div className='advance__rule__engine__remote__command__checkbox__container'>
                        <div className='advance__rule__engine__remote__command__any__all-checkbox'
                        onClick={()=>{
                            if(!connectorLevelRC?.has(formData?.remoteCommandType) && !(formData?.remoteCommandType === 'FTP_PERIPHERAL_CARD_SW_UPDATE' && formData?.peripheralCardType && connectorLevelPeripheralCardTypes.has(formData?.peripheralCardType)))return;
                            const data = { ...formData };
                            data.connectorScope = data.connectorScope === 'any' || data.connectorScope == 'null' ? 'all' : 'any';
                            setFormData(data);
                        }}
                        >
                            <CheckBox 
                                checked={formData?.connectorScope === 'all'}  
                                isDisabled = {!connectorLevelRC?.has(formData?.remoteCommandType) && !(formData?.remoteCommandType === 'FTP_PERIPHERAL_CARD_SW_UPDATE' && formData?.peripheralCardType && connectorLevelPeripheralCardTypes.has(formData?.peripheralCardType))} 
                            />
                        </div>
                        <div className='advance__rule__engine__set__parameter__enable__disable__offset-label'>Execute for all connectors</div>
                    </div>
                }
            </div>
            <div className='advance__rule__engine__side__popover-footer'>
                <CustomButtonGroup
                    buttonsList={btnsList}
                    buttonGroupClassName='button__group__footer' />
            </div>
        </>
    )
}

export default RemoteCommandSidePopover;